/**
 * Central tier name constants.
 *
 * Use these helpers anywhere in the UI to get the user-visible display name
 * for a tariff, so that the names stay consistent across the whole app.
 *
 * Logic/keys should use stable internal identifiers: "free", "lite", "pro".
 * User-facing strings must always be one of: "Бесплатно", "ЛАЙТ", "ПРО".
 */

// Numeric tier level → internal stable key
export const TIER_KEYS = {
  1: 'free',
  2: 'lite',
  3: 'pro',
};

// Numeric tier level → user-visible Russian name
export const TIER_DISPLAY_NAMES = {
  1: 'Бесплатно',
  2: 'Пробный доступ', // 14 дней за регистрацию (прежнее название «ЛАЙТ» снято)
  3: 'ПРО',
};

// Reverse lookup: any known variant → tier number
const TIER_BY_ANY_VALUE = {
  // numbers (stringified)
  '1': 1, '2': 2, '3': 3, '4': 3,
  // new canonical keys
  free: 1, lite: 2, pro: 3,
  FREE: 1, LITE: 2, PRO: 3,
  // legacy tier-name strings
  ASTROFEEL: 2, astrofeel: 2, АСТРОфил: 2,
  PRO_LIFETIME: 3,
};

/**
 * Convert any known representation (number, internal key, legacy name) to tier number (1/2/3).
 * Unknown values fall back to 1 (FREE).
 */
export function toTierNumber(value) {
  if (value === null || value === undefined) return 1;
  if (typeof value === 'number') return value >= 1 && value <= 3 ? value : (value === 4 ? 3 : 1);
  const mapped = TIER_BY_ANY_VALUE[value];
  return mapped || 1;
}

/**
 * Convert any known representation to the canonical internal key ("free" | "lite" | "pro").
 */
export function toTierKey(value) {
  return TIER_KEYS[toTierNumber(value)] || 'free';
}

/**
 * Convert any known representation to the user-visible Russian name.
 */
export function toTierDisplayName(value) {
  return TIER_DISPLAY_NAMES[toTierNumber(value)] || 'Бесплатно';
}

// Convenience constants
export const TIER_FREE = { key: 'free', name: 'Бесплатно', level: 1 };
export const TIER_LITE = { key: 'lite', name: 'Пробный доступ', level: 2 };
export const TIER_PRO = { key: 'pro', name: 'ПРО', level: 3 };

/**
 * ПРО у человека уже есть — срочный или бессрочный.
 *
 * Одно правило на все витрины: где показываем «входит в твой тариф» вместо
 * кнопки оплаты и где прячем счётчик бесплатных дней. Три источника подряд,
 * потому что доступ приходит тремя разными путями и ни один не покрывает
 * остальные: effective_tier — обычный расчёт, effective_tier_name — ответ
 * старых ручек без числа, is_lifetime — «навсегда», у которого срока нет
 * вовсе и days_remaining приходит фиктивным числом (99999).
 */
export function isProOrLifetime(user) {
  if (!user) return false;
  const level = user.effective_tier || user.access_level || 1;
  return level >= TIER_PRO.level
    || user.effective_tier_name === 'PRO'
    || user.effective_tier_name === 'PRO_LIFETIME'
    || !!user.is_lifetime;
}
